import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';
import { API_BASE_URL } from "../constants/api";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

export default function BonsLivraison() {
    const navigate = useNavigate();
    const [bonsLivraison, setBonsLivraison] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_livraison', direction: 'desc' });
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // États pour le modal de confirmation
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmVariant, setConfirmVariant] = useState('primary');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        fetchBonsLivraison();
        fetchStats();
        fetchSocieteInfos();
        fetchTaxes();
        loadLogoBase64();
    }, [filtreStatut]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const openConfirmModal = (id, title, message, variant = 'primary', action) => {
        setConfirmId(id);
        setConfirmTitle(title);
        setConfirmMessage(message);
        setConfirmVariant(variant);
        setConfirmAction(() => action);
        setShowConfirmModal(true);
    };

    const fetchBonsLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/bons_livraison.php`;
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBonsLivraison(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const fetchBonLivraisonDetails = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement détails");
            
            const result = await response.json();
            return result.data;
        } catch (err) {
            console.error("Erreur:", err);
            showError("Impossible de charger les détails du bon de livraison");
            return null;
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            prepare: { color: '#3b82f6', icon: 'ri-stack-line', label: 'Préparé' },
            livre: { color: '#10b981', icon: 'ri-check-double-line', label: 'Livré' },
            annule: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge" style={{ 
                background: `${config.color}15`, 
                color: config.color 
            }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const estEnRetard = (dateLivraison) => {
        return new Date(dateLivraison) < new Date();
    };

    // Filtrage et tri des données
    const filteredBons = useMemo(() => {
        return bonsLivraison
            .filter(b => 
                b.numero_bl?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.code_client?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.destinataire?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_livraison') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_livraison) - new Date(b.date_livraison)
                        : new Date(b.date_livraison) - new Date(a.date_livraison);
                }
                if (sortConfig.key === 'numero_bl') {
                    return sortConfig.direction === 'asc'
                        ? (a.numero_bl || '').localeCompare(b.numero_bl || '')
                        : (b.numero_bl || '').localeCompare(a.numero_bl || '');
                }
                return 0;
            });
    }, [bonsLivraison, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredBons.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBons.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleMarquerLivre = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la livraison",
            "Êtes-vous sûr de vouloir marquer ce bon de livraison comme <strong>livré</strong> ? Cette action mettra à jour le statut.",
            "success",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(
                        `${API_BASE_URL}/bons_livraison.php`,
                        {
                            method: 'POST',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                action: 'marquer_livre',
                                id: confirmId
                            })
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("✅ Bon de livraison marqué comme livré");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la mise à jour");
                    }
                } catch (err) {
                    console.error("Erreur:", err);
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    const genererHTMLImpression = (bl) => {
        if (!bl) return '';

        const statutConfig = {
            brouillon: { class: 'draft', label: 'BROUILLON' },
            prepare: { class: 'prepared', label: 'PRÉPARÉ' },
            livre: { class: 'delivered', label: 'LIVRÉ' },
            annule: { class: 'cancelled', label: 'ANNULÉ' }
        };

        const statut = statutConfig[bl.statut] || statutConfig.brouillon;
        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + (parseInt(l.quantite) || 0), 0) || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bon de livraison ${bl.numero_bl || ''}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        background: #eef2f7;
                        padding: 20px;
                        color: #1e293b;
                        font-size: 11px;
                    }
                    .document-container {
                        margin: 0 auto;
                        background: white;
                        border-radius: 12px;
                        overflow: hidden;
                    }
                    
                    .document-header {
                        background: white;
                        padding: 5px;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .header-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 15px;
                    }
                    .logo-area {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .logo-img {
                        width: auto;
                        height: 60px;
                    }
                    .logo-fallback {
                        width: 50px;
                        height: 50px;
                        background: linear-gradient(135deg, #1a3c5e, #2c5a7a);
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                        font-weight: bold;
                        color: white;
                    }
                    .company-name {
                        font-size: 16px;
                        font-weight: 700;
                        color: #1a3c5e;
                    }
                    .document-title {
                        text-align: right;
                    }
                    .document-title h2 {
                        font-size: 18px;
                        font-weight: 700;
                        color: #1a3c5e;
                        letter-spacing: 1px;
                        margin-bottom: 4px;
                    }
                    .document-number {
                        font-size: 10px;
                        color: #64748b;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 3px 10px;
                        border-radius: 20px;
                        font-size: 9px;
                        font-weight: 600;
                        margin-top: 6px;
                    }
                    .status-delivered { background: #d1fae5; color: #065f46; }
                    .status-prepared { background: #dbeafe; color: #1e40af; }
                    .status-draft { background: #f1f5f9; color: #475569; }
                    .status-cancelled { background: #fee2e2; color: #991b1b; }
                    
                    .company-details {
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #64748b;
                        padding: 5px;
                        border-top: 1px solid #e2e8f0;
                    }
                    
                    .document-body {
                        padding: 5px 0px;
                    }
                    
                    .client-card {
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                        margin-bottom: 20px;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        border: 1px solid #e2e8f0;
                    }
                    .info-label {
                        font-size: 9px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #64748b;
                        margin-bottom: 6px;
                    }
                    .client-name {
                        font-size: 14px;
                        font-weight: 700;
                        color: #0f172a;
                        margin-bottom: 6px;
                    }
                    .client-details {
                        font-size: 10px;
                        color: #475569;
                        line-height: 1.4;
                    }
                    .date-item {
                        margin-bottom: 6px;
                    }
                    .date-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    .date-value {
                        font-size: 11px;
                        font-weight: 600;
                        color: #0f172a;
                    }
                    
                    .delivery-card {
                        background: #f8fafc;
                        border-radius: 0px;
                        padding: 15px;
                        margin-bottom: 20px;
                        border: 1px solid #e2e8f0;
                        border-left: 2px solid #1a3c5e;
                    }
                    .delivery-title {
                        font-size: 11px;
                        font-weight: 600;
                        margin-bottom: 10px;
                        color: #1e293b;
                    }
                    .delivery-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 10px;
                    }
                    .delivery-item strong {
                        display: block;
                        font-size: 8px;
                        color: #64748b;
                        margin-bottom: 3px;
                    }
                    .delivery-item span {
                        font-size: 10px;
                        color: #1e293b;
                    }
                    
                    .products-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                        font-size: 10px;
                    }
                    .products-table th {
                        background: #f8fafc;
                        padding: 8px 10px;
                        text-align: left;
                        font-weight: 600;
                        color: #1e293b;
                        border-bottom: 1.5px solid #e2e8f0;
                    }
                    .products-table td {
                        padding: 8px 10px;
                        border-bottom: 1px solid #e2e8f0;
                        color: #334155;
                    }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    
                    .totals {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 15px;
                    }
                    .totals-card {
                        width: 280px;
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                    }
                    .total-line {
                        display: flex;
                        justify-content: space-between;
                        padding: 5px 0;
                        font-size: 10px;
                        border-bottom: 1px dashed #e2e8f0;
                    }
                    .total-line:last-child {
                        border-bottom: none;
                    }
                    .total-line.highlight {
                        background: #eef2ff;
                        margin: 8px -15px;
                        padding: 8px 15px;
                        font-weight: 600;
                    }
                    
                    .notes-box {
                        margin-top: 15px;
                        padding: 10px;
                        background: #fffbeb;
                        border-left: 3px solid #f59e0b;
                        border-radius: 6px;
                        font-size: 9px;
                    }
                    
                    .signature-section {
                        margin-top: 25px;
                        padding-top: 15px;
                        border-top: 1px solid #e2e8f0;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-end;
                    }
                    .signature-box {
                        text-align: center;
                    }
                    .signature-line {
                        width: 150px;
                        height: 1px;
                        background: #1a3c5e;
                        margin-bottom: 6px;
                    }
                    .signature-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    
                    .document-footer {
                        background: #f8fafc;
                        padding: 12px 25px;
                        text-align: center;
                        font-size: 8px;
                        color: #64748b;
                        border-top: 1px solid #e2e8f0;
                    }
                    
                    @media print {
                        body { background: white; padding: 0; }
                        .document-container { box-shadow: none; border-radius: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="document-container">
                    <div class="document-header">
                        <div class="header-top">
                            <div class="logo-area">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="document-title">
                                <h2>BON DE LIVRAISON</h2>
                                <div class="document-number">N° ${bl.numero_bl || ''}</div>
                                <div class="status-badge status-${statut.class}">${statut.label}</div>
                            </div>
                        </div>
                        <div class="company-details">
                            <table style="width: 100%; border: 0px;">
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0; width: 50%;">
                                        <strong>Tél:</strong> ${company.phone}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0; width: 50%;">
                                        <strong>Adresse:</strong> ${company.address}
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0;">
                                        <strong>Email:</strong> ${company.email}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0;">
                                        <strong>Site web:</strong> ${company.site_web}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    
                    <div class="document-body">
                        <div class="client-card">
                            <div>
                                <div class="info-label">CLIENT</div>
                                <div class="client-name">${bl.client_nom || 'Client non spécifié'}</div>
                                <div class="client-details">
                                    <div>Code: ${bl.code_client || ''}</div>
                                    ${bl.client_telephone ? `<div> ${bl.client_telephone}</div>` : ''}
                                    ${bl.client_email ? `<div>✉ ${bl.client_email}</div>` : ''}
                                    ${bl.client_adresse ? `<div>${bl.client_adresse}</div>` : ''}
                                </div>
                            </div>
                            <div class="dates">
                                <div class="date-item">
                                    <div class="date-label">Date de livraison</div>
                                    <div class="date-value">${formatDate(bl.date_livraison)}</div>
                                </div>
                                ${bl.numero_facture ? `
                                    <div class="date-item">
                                        <div class="date-label">Facture associée</div>
                                        <div class="date-value">${bl.numero_facture}</div>
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                        
                        <div class="delivery-card">
                            <div class="delivery-title">
                               INFORMATIONS DE LIVRAISON
                            </div>
                            <div class="delivery-grid">
                                <div class="delivery-item">
                                    <strong>Destinataire</strong>
                                    <span>${bl.destinataire || bl.client_nom || 'Non spécifié'}</span>
                                </div>
                                <div class="delivery-item">
                                    <strong>Adresse de livraison</strong>
                                    <span>${bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</span>
                                </div>
                            </div>
                        </div>
                        
                       
                        
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th>Lot</th>
                                    <th>Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0 
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td><span style="font-family: monospace;">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center fw-bold">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '-'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align: center; padding: 20px;">Aucun produit dans ce bon de livraison</td></tr>'
                                }
                            </tbody>
                        </table>
                        
                        <div class="totals">
                            <div class="totals-card">
                                <div class="total-line">
                                    <span>Nombre de références</span>
                                    <span><strong>${bl?.lignes?.length || 0}</strong></span>
                                </div>
                                <div class="total-line highlight">
                                    <span>Quantité totale</span>
                                    <span><strong style="color: #1a3c5e;">${totalProduits}</strong></span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Cachet et signature du transporteur</div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Cachet et signature du client</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="document-footer">
                        <div>Merci de votre confiance</div>
                        <div style="margin-top: 4px;">${company.name} • ${company.phone} • ${company.email}</div>
                        <div style="margin-top: 4px;">RC: ${company.rc} | NIF: ${company.nif} | Patente: ${company.patente}</div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = async (bl) => {
        try {
            let blData = bl;
            if (!bl.lignes || bl.lignes.length === 0) {
                const details = await fetchBonLivraisonDetails(bl.id_bon_livraison);
                if (details) {
                    blData = details;
                }
            }
            
            const htmlContent = genererHTMLImpression(blData);
            
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            
            iframe.contentWindow.onload = () => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };
            
            showSuccess("Document envoyé à l'impression");
        } catch (err) {
            console.error("Erreur impression:", err);
            showError("Impossible de générer le bon de livraison");
        }
    };

    const handlePreparer = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la préparation",
            "Êtes-vous sûr de vouloir préparer ce bon de livraison ? Les produits seront déduits du stock.",
            "primary",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const userStr = localStorage.getItem("user");
                    const user = userStr ? JSON.parse(userStr) : null;
                    
                    const getResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );

                    if (!getResponse.ok) throw new Error("Erreur chargement du BL");
                    
                    const getResult = await getResponse.json();
                    const blData = getResult.data;
                    
                    const updateResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        {
                            method: 'PUT',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                ...blData,
                                statut: 'prepare',
                                id_utilisateur: user?.id || user?.id_utilisateur || 1
                            })
                        }
                    );

                    const updateResult = await updateResponse.json();

                    if (updateResult.success) {
                        setShowConfirmModal(false);
                        showSuccess(" Bon de livraison marqué comme préparé");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(updateResult.message || "Erreur lors de la mise à jour");
                    }
                } catch (err) {
                    console.error("Erreur:", err);
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handleAnnuler = async (id) => {
        openConfirmModal(
            id,
            "Confirmer l'annulation",
            "Êtes-vous sûr de vouloir annuler ce bon de livraison ? Cette action est irréversible.",
            "danger",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    
                    const getResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );

                    if (!getResponse.ok) throw new Error("Erreur chargement du BL");
                    
                    const getResult = await getResponse.json();
                    const blData = getResult.data;
                    
                    const updateResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        {
                            method: 'PUT',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                ...blData,
                                statut: 'annule'
                            })
                        }
                    );

                    const updateResult = await updateResponse.json();

                    if (updateResult.success) {
                        setShowConfirmModal(false);
                        showSuccess("❌ Bon de livraison annulé");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(updateResult.message || "Erreur lors de l'annulation");
                    }
                } catch (err) {
                    console.error("Erreur:", err);
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon} style={{ fontSize: '1.1rem' }}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading && !bonsLivraison.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des bons de livraison...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !bonsLivraison.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchBonsLivraison}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-truck-line fs-20 text-primary"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des bons de livraison</h4>
                                    <p className="text-muted small mb-0">Gérez vos bons de livraison et suivez les expéditions</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                style={{ background: '#21ACCD', border: '0px' }}
                                onClick={() => navigate('/ventes/bl/nouveau')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-add-line"></i>
                                Nouveau BL
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><i className="ri-truck-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">BL (mois)</span>
                                        <h3 className="kpi-value">{stats.mois?.nb_bl || 0}</h3>
                                        <span className="kpi-unit">{stats.mois?.nb_produits || 0} produits</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><i className="ri-stack-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">À livrer</span>
                                        <h3 className="kpi-value">{stats.statuts?.find(s => s.statut === 'prepare')?.nb || 0}</h3>
                                        <span className="kpi-unit">Préparés</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><i className="ri-check-double-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Livrés</span>
                                        <h3 className="kpi-value">{stats.statuts?.find(s => s.statut === 'livre')?.nb || 0}</h3>
                                        <span className="kpi-unit">Terminés</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-info">
                                    <div className="kpi-icon"><i className="ri-calendar-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Aujourd'hui</span>
                                        <h3 className="kpi-value">{stats.aujourdhui?.nb || 0}</h3>
                                        <span className="kpi-unit">À livrer</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher BL, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="rounded-pill px-3"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="brouillon">Brouillons</option>
                                        <option value="prepare">Préparés</option>
                                        <option value="livre">Livrés</option>
                                        <option value="annule">Annulés</option>
                                    </Form.Select>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                            <i className="ri-stack-line me-1"></i>
                                            {filteredBons.length} BL(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des BL */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° BL</th>
                                            <th>Date livraison</th>
                                            <th>Client</th>
                                            <th>Destinataire</th>
                                            <th>Facture</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" />
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((bl) => {
                                                const retard = estEnRetard(bl.date_livraison) && bl.statut !== 'livre' && bl.statut !== 'annule';
                                                
                                                return (
                                                    <tr key={bl.id_bon_livraison} className={retard ? 'table-danger' : ''}>
                                                        <td className="ps-4 fw-semibold">
                                                            <Link to={`/ventes/bl/${bl.id_bon_livraison}`} className="table-link">
                                                                {bl.numero_bl?.length > 12 
                                                                    ? bl.numero_bl.substring(0, 12) + '...' 
                                                                    : bl.numero_bl}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            {formatDate(bl.date_livraison)}
                                                            {retard && (
                                                                <Badge bg="danger" className="ms-2 retard-badge" style={{ fontSize: '0.65rem' }}>
                                                                    Retard
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{bl.client_nom}</div>
                                                            <small className="text-muted">{bl.code_client}</small>
                                                        </td>
                                                        <td>{bl.destinataire || '-'}</td>
                                                        <td>
                                                            {bl.numero_facture ? (
                                                                <Link to={`/ventes/factures/${bl.id_facture}`} className="text-decoration-none">
                                                                    <small>{bl.numero_facture}</small>
                                                                </Link>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>{getStatutBadge(bl.statut)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/blDetails/${bl.id_bon_livraison}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {bl.statut === 'brouillon' && (
                                                                    <>
                                                                        <ActionButton
                                                                            variant="outline-primary"
                                                                            icon="ri-pencil-line"
                                                                            onClick={() => navigate(`/ventes/bl/modifier/${bl.id_bon_livraison}`)}
                                                                            tooltip="Modifier"
                                                                        />
                                                                        <ActionButton
                                                                            variant="outline-success"
                                                                            icon="ri-stack-line"
                                                                            onClick={() => handlePreparer(bl.id_bon_livraison)}
                                                                            tooltip="Préparer"
                                                                        />
                                                                    </>
                                                                )}
                                                                {bl.statut !== 'livre' && bl.statut !== 'annule' && (
                                                                    <ActionButton
                                                                        variant="outline-danger"
                                                                        icon="ri-close-circle-line"
                                                                        onClick={() => handleAnnuler(bl.id_bon_livraison)}
                                                                        tooltip="Annuler"
                                                                    />
                                                                )}
                                                                <ActionButton
                                                                    variant="outline-secondary"
                                                                    icon="ri-printer-line"
                                                                    onClick={() => handlePrint(bl)}
                                                                    tooltip="Imprimer"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <p className="text-muted mb-0">Aucun bon de livraison trouvé</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredBons.length)} sur {filteredBons.length} BL(s)
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* MODAL DE CONFIRMATION */}
                <Modal 
                    show={showConfirmModal} 
                    onHide={() => setShowConfirmModal(false)} 
                    centered 
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0" style={{background:'#1DACCE'}}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    {confirmVariant === 'success' && (
                                        <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '4rem' }}></i>
                                    )}
                                    {confirmVariant === 'danger' && (
                                        <i className="ri-alert-line text-danger" style={{ fontSize: '4rem' }}></i>
                                    )}
                                    {confirmVariant === 'warning' && (
                                        <i className="ri-error-warning-line text-warning" style={{ fontSize: '4rem' }}></i>
                                    )}
                                    {confirmVariant === 'primary' && (
                                        <i className="ri-information-line text-primary" style={{ fontSize: '4rem' }}></i>
                                    )}
                                </div>
                                <h3 className="fw-bold">{confirmTitle}</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2 text-center">
                        <p className="text-muted mb-4" dangerouslySetInnerHTML={{ __html: confirmMessage }}></p>
                        
                        {confirmVariant === 'success' && (
                            <Alert variant="info" className="text-start mb-4">
                                <div className="d-flex align-items-center">
                                    <i className="ri-information-line fs-4 me-3"></i>
                                    <div>
                                        <small className="d-block">✓ Marquer le bon comme livré</small>
                                        <small className="d-block">✓ Enregistrer la date de livraison</small>
                                    </div>
                                </div>
                            </Alert>
                        )}
                        
                        {confirmVariant === 'danger' && (
                            <Alert variant="warning" className="text-start mb-4">
                                <i className="ri-alert-line me-2"></i>
                                Cette action est irréversible.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowConfirmModal(false)}
                            className="px-4"
                            size="lg"
                            disabled={isProcessing}
                        >
                            <i className="ri-close-line me-2"></i>
                            Annuler
                        </Button>
                        <Button 
                            variant={confirmVariant} 
                            onClick={() => {
                                if (confirmAction && confirmId) {
                                    confirmAction(confirmId);
                                }
                            }} 
                            className="px-5"
                            size="lg"
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className={`ri-${
                                        confirmVariant === 'success' ? 'check-double' :
                                        confirmVariant === 'danger' ? 'delete-bin' :
                                        confirmVariant === 'warning' ? 'alert' :
                                        'check'
                                    }-line me-2`}></i>
                                    Confirmer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0dcaf0; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e8f0fe; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                .btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Retard Badge */
                .retard-badge {
                    animation: pulse 2s infinite;
                }
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                /* Utilitaires */
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>   
        </React.Fragment>
    );
}